/**
 * Ambassador Section Component
 * 
 * A dedicated section component for showcasing individual brand ambassadors
 * following the same design patterns as other sections on the WoodAlps website.
 * Each ambassador gets a full-width section with their story, photos, and details.
 * 
 * Features:
 * - Consistent section-based layout
 * - Portrait and action photo display
 * - Rich story content with proper typography
 * - Social media links integration
 * - Responsive design for all devices
 * - Alternating layouts (left/right orientation)
 * - Bike category and experience display
 * 
 * @param {object} data - Ambassador data including name, story, images, etc.
 * @param {boolean} reverse - Whether to reverse the layout (image right, content left)
 * @param {string} bgColor - Background color class for the section
 * 
 * @description Individual ambassador showcase section with rich content and photos
 */

import Image from 'next/image';
import { sanityLoader } from '../../utils/sanityLoader';
import { Instagram, Globe, MapPin, Calendar, Bike, Mail, Phone, Facebook } from 'lucide-react';
import { useState } from 'react';
import ImageModal from '../ui/ImageModal';
import { useTranslation } from '../../src/hooks/useTranslation';

const AmbassadorSection = ({ data, reverse = false, bgColor = 'bg-white' }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const {t} = useTranslation();
  
  // Helper function to translate bike category
  const getTranslatedBikeCategory = (category) => {
    switch (category) {
      case 'Road':
        return t('ambassador_section.bike_category_road');
      case 'Gravel':
        return t('ambassador_section.bike_category_gravel');
      case 'MTB':
        return t('ambassador_section.bike_category_mtb');
      default:
        return category;
    }
  };
  
  if (!data) return null;

  const {
    name,
    title,
    location,
    yearsWithWoodAlps,
    bikeCategory,
    bikeSize,
    quote,
    portraitImage,
    actionImages = [],
    story,
    socialLinks = {},
    bikeName
  } = data;

  // Create array of all images for modal navigation
  const allImages = [portraitImage, ...(actionImages || [])].filter(Boolean);

  const handleImageClick = (imageIndex) => {
    setCurrentImageIndex(imageIndex);
    setSelectedImage(allImages[imageIndex]);
  };

  const handlePrevImage = () => {
    const newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : allImages.length - 1;
    setCurrentImageIndex(newIndex);
    setSelectedImage(allImages[newIndex]);
  };

  const handleNextImage = () => {
    const newIndex = currentImageIndex < allImages.length - 1 ? currentImageIndex + 1 : 0;
    setCurrentImageIndex(newIndex);
    setSelectedImage(allImages[newIndex]);
  };

  return (
    <section className={`section ${bgColor}`}>
      <div className="container-custom">
        <div className={`flex flex-col ${reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12 lg:gap-20`}>
          
          {/* Image Side */}
          <div className="w-full lg:w-1/2">
            {/* Portrait Image with Quote Overlay */}
            <div className="relative mb-8">
              <div 
                className="relative h-96 md:h-[500px] rounded-lg overflow-hidden cursor-pointer hover:scale-105 transition-transform duration-300"
                onClick={() => handleImageClick(0)}
              >
                <Image
                  src={portraitImage?.src || '/images/grootybike.jpg'}
                  alt={portraitImage?.alt || `${name} portrait`}
                  fill
                  loader={sanityLoader}
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  className="object-cover"
                />
                {/* Quote Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                    <div className="flex items-start">
                      <div className="text-accent-color mr-2 mt-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                        </svg>
                      </div>
                      <p className="text-white text-lg md:text-xs font-neuropol leading-relaxed">
                        {quote}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Click to enlarge indicator */}
                <div className="absolute top-4 right-4 bg-black/50 rounded-full p-2">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Action Images Gallery */}
            {actionImages?.length > 0 && (
              <div className="grid grid-cols-3 gap-4">
                {(actionImages || []).map((image, index) => (
                  <div 
                    key={index} 
                    className="relative h-24 md:h-32 rounded-lg overflow-hidden cursor-pointer hover:scale-105 transition-transform duration-300"
                    onClick={() => handleImageClick(index + 1)}
                  >
                    <Image
                      src={image.src}
                      alt={image.alt || `${name} action photo ${index + 1}`}
                      fill
                      loader={sanityLoader}
                      sizes="(max-width: 1024px) 33vw, 17vw"
                      className="object-cover"
                    />
                    {/* Click to enlarge indicator */}
                    <div className="absolute top-2 right-2 bg-black/50 rounded-full p-1">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Content Side */}
          <div className="w-full lg:w-1/2">
            {/* Ambassador Details */}
            <div className="mb-6">
              <h2 className="text-3xl md:text-4xl font-bold text-deep-black mb-2 font-neuropol">
                {name}
              </h2>
              <p className="text-xl text-accent-color font-roboto  mb-4">
                {title}
              </p>
              
              {/* Info Pills */}
              <div className="flex flex-wrap gap-3 mb-6">
                <div className="flex items-center bg-soft-white px-3 py-2 rounded-full">
                  <MapPin size={16} className="text-forest-green mr-2" />
                  <span className="text-sm font-roboto">{location}</span>
                </div>
                <div className="flex items-center bg-soft-white px-3 py-2 rounded-full">
                  <Calendar size={16} className="text-forest-green mr-2" />
                  <span className="text-sm font-roboto">{yearsWithWoodAlps} {yearsWithWoodAlps <= 1 ? t('ambassador_section.years_with_woodalps_singular') : t('ambassador_section.years_with_woodalps')}</span>
                </div>
                <div className="flex items-center bg-soft-white px-3 py-2 rounded-full">
                  <Bike size={16} className="text-forest-green mr-2" />
                                   <span className="text-sm font-roboto">{getTranslatedBikeCategory(bikeCategory)} • {bikeName} • {bikeSize} </span>
                </div>
              </div>
            </div>

            {/* Story Content */}
            {story && story.trim() && (
              <div className="mb-8">
                <h3 className="text-xl font-bold text-deep-black mb-4 font-neuropol">
                  {t('ambassador_section.their_story')}
                </h3>
                <div className="prose prose-lg max-w-none">
                  {story.split('\n\n').map((paragraph, index) => (
                    <p key={index} className="text-gray-700 font-roboto leading-relaxed mb-4">
                      {paragraph.trim()}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Information */}
             
            <div className="border-t border-gray-200 pt-6">
              {/* Social Media Icons */}
              {(socialLinks?.instagram || socialLinks?.facebook || socialLinks?.strava || socialLinks?.website) && (
                <div className="flex gap-3">
                  {socialLinks.instagram && socialLinks.instagram.trim() && (
                    <a
                      href={`https://instagram.com/${socialLinks.instagram.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90 transition-opacity"
                      aria-label="Instagram"
                    >
                      <Instagram size={18} />
                    </a>
                  )}
                  {socialLinks.facebook && socialLinks.facebook.trim() && (
                    <a
                      href={`https://facebook.com/${socialLinks.facebook}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                      aria-label="Facebook"
                    >
                      <Facebook size={18} />
                    </a>
                  )}
                  {socialLinks.strava && socialLinks.strava.trim() && (
                    <a
                      href={`https://strava.com/athletes/${socialLinks.strava}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                      aria-label="Strava"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.599h4.172L10.463 0l-7 13.828h4.172"/>
                      </svg>
                    </a>
                  )}
                  {socialLinks.website && socialLinks.website.trim() && (
                    <a
                      href={socialLinks.website.startsWith('http') ? socialLinks.website : `https://${socialLinks.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-10 h-10 rounded-full bg-forest-green text-white hover:bg-forest-green/90 transition-colors"
                      aria-label="Website"
                    >
                      <Globe size={18} />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <ImageModal 
          image={selectedImage.src} 
          alt={selectedImage.alt || `${name} photo`}
          onClose={() => setSelectedImage(null)}
          onPrev={allImages.length > 1 ? handlePrevImage : null}
          onNext={allImages.length > 1 ? handleNextImage : null}
          showNavigation={allImages.length > 1}
        />
      )}
    </section>
  );
};

export default AmbassadorSection;
